/**
 * Картинка.
 *
 * Самый частый слой: реакции, рамки, логотипы. И самый показательный
 * для контракта — именно на нём видно, зачем `update` обязан менять
 * узел, а не пересоздавать его: смена размера пересозданием заставила
 * бы браузер грузить ту же картинку заново, и слой моргнул бы пустотой
 * прямо в эфире.
 */
import { imageBytes, warmCache } from './warm.js';
import { asRecord, numberInRange, oneOf, url } from './props.js';
const TYPE = 'image';
/** Как картинка вписывается в отведённый прямоугольник. */
const FITS = ['contain', 'cover', 'fill'];
export const imageWidget = {
    type: TYPE,
    parse(raw) {
        const props = asRecord(TYPE, raw);
        return {
            src: url(TYPE, props, 'src'),
            fit: oneOf(TYPE, props, 'fit', FITS, 'contain'),
            opacity: numberInRange(TYPE, props, 'opacity', 0, 1, 1),
            alt: typeof props.alt === 'string' ? props.alt : '',
        };
    },
    mount(props) {
        const el = document.createElement('div');
        el.dataset.widget = TYPE;
        const img = document.createElement('img');
        // Декодирование в стороне от основного потока: иначе крупная
        // картинка подвешивает отрисовку, а в эфире это видно как рывок
        img.decoding = 'async';
        // Атрибутом, а не свойством: свойство отражается в атрибут
        // не во всех реализациях DOM, и проверить его в тесте нечем.
        // Само отключение нужно редактору: перетаскивание картинки —
        // поведение браузера по умолчанию, и вместо слоя человек тащил
        // бы саму картинку
        img.setAttribute('draggable', 'false');
        el.append(img);
        // Незагрузившийся ассет обязан быть видимым.
        //
        // Оверлей работает без присмотра во время эфира: если картинка
        // не пришла, на её месте будет пустота, и стример узнает об этом
        // от зрителей. Отметка в DOM даёт панели повод сказать
        // «ассет не загрузился» (docs/02) — и это единственный слушатель
        // во всём примитиве, а значит единственное, что надо снять.
        const onError = () => {
            el.dataset.failed = 'true';
        };
        const onLoad = () => {
            delete el.dataset.failed;
        };
        img.addEventListener('error', onError);
        img.addEventListener('load', onLoad);
        apply(el, img, props);
        return {
            el,
            update(next) {
                apply(el, img, next);
            },
            destroy() {
                img.removeEventListener('error', onError);
                img.removeEventListener('load', onLoad);
                // Пустой src останавливает загрузку, если она ещё идёт:
                // уничтоженный слой не должен тянуть сеть
                img.removeAttribute('src');
                el.remove();
            },
        };
    },
    /**
     * Кладёт картинку в прогрев, не создавая узла.
     *
     * Ради этого и разделены `preload` и `mount`: припаркованное
     * за кадром должно появиться мгновенно, когда его выведут в кадр,
     * но до тех пор ничего не стоить.
     *
     * ЧЕРЕЗ ОБЩИЙ КЕШ, А НЕ ПРОСТО `new Image()`: кеш браузера
     * не наш, его чистят когда угодно, а главное — он не знает
     * про бюджет в 150 МБ. Картинка, декодированная в растр,
     * занимает площадь×4 байта, и десяток фонов 4K съел бы память
     * машины стримера прямо во время эфира (docs/04).
     */
    preload(props) {
        const cache = warmCache();
        if (cache !== null && cache.has(props.src))
            return;
        const img = new Image();
        img.decoding = 'async';
        // Чужой origin без CORS даёт картинку, которую нельзя ни
        // измерить, ни положить на canvas: прогрев такой бесполезен
        img.crossOrigin = 'anonymous';
        img.src = props.src;
        if (cache === null)
            return;
        // decode(), а не событие load: загруженная, но не декодированная
        // картинка всё равно даст паузу в первый кадр показа — а это
        // ровно то, ради чего прогрев и делается
        void img
            .decode()
            .then(() => {
            cache.put(props.src, { kind: 'image', el: img }, imageBytes(img.naturalWidth, img.naturalHeight));
        })
            .catch(() => {
            // Не скачалась или не декодируется. Молча: прогрев —
            // это ускорение, а не условие показа, и слой при выводе
            // в кадр попробует ещё раз сам
        });
    },
};
/**
 * Переносит свойства в уже существующие узлы.
 *
 * Присваивание идёт ТОЛЬКО при изменении: запись того же значения
 * в src заставляет браузер перезапросить картинку, даже если адрес
 * совпадает.
 */
function apply(el, img, props) {
    /* eslint-disable no-param-reassign --
       Правило бережёт неизменяемость СОСТОЯНИЯ, а здесь меняется DOM —
       ровно то, ради чего функция и существует. Требование
       иммутабельности относится к сцене, а не к узлам, которыми
       её рисуют. Тот же случай, что в apps/overlay/src/render.ts. */
    if (img.getAttribute('src') !== props.src) {
        img.setAttribute('src', props.src);
    }
    if (img.alt !== props.alt) {
        img.alt = props.alt;
    }
    img.style.objectFit = props.fit;
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.display = 'block';
    el.style.opacity = String(props.opacity);
    /* eslint-enable no-param-reassign */
}
//# sourceMappingURL=image.js.map